import React from 'react';
import { Link } from 'react-router-dom';

import Seo from '../ui/Seo';
import Reveal from '../ui/Reveal';
import PageHero from '../sections/PageHero';
import TradeTicker from '../sections/TradeTicker';
import SectionHead from '../sections/SectionHead';
import Stats from '../sections/Stats';
import PowerPath from '../sections/PowerPath';
import ProcessRail from '../sections/ProcessRail';
import MeasurementCatalog from '../sections/MeasurementCatalog';
import Bento from '../sections/Bento';
import ServiceList from '../sections/ServiceList';
import Faqs from '../sections/Faqs';
import Cta from '../sections/Cta';
import { contact, site } from '../data/site';
import { measurementGroups } from '../data/measurements';
import { DEMO_PATH, demoLink, EXPLAINER_PATH, SCREENS, SCREEN_GROUPS } from '../demo/screens';
import { THRESHOLDS } from '../demo/model';

import SwitchgearLineups from '../media/images/tpi/switchgear-lineups.jpg';

/* The product page for SmartDC, our monitoring system. It describes what the system
   does. It does not claim installed sites, customers, certifications or measured
   results, because there are none to point to yet: anything like that needs a
   real source first. The demo runs on simulated readings, and wherever this
   page mentions the demo it says so.

   Nothing here says TPI builds or sells this. The TPI page links here because
   this watches the kind of equipment TPI builds and we install; the rule at the
   top of tpi.jsx applies to anything added about TPI's part in it. */

const reference = [
    { label: 'AC distribution', detail: 'Three-phase, four-wire wye; 400/230 V · 50 Hz is also modeled', value: '480/277 V · 60 Hz' },
    { label: 'UPS operating mode', detail: 'Input, rectifier, DC link, inverter, AC distribution; bypass energized and unloaded', value: 'Double conversion' },
    { label: 'UPS DC link', detail: 'Two-wire +/− with PE; floating IT; no neutral', value: '800 VDC pole-to-pole' },
    { label: 'Topology', detail: 'Six inverter sections per path; no DC tie between A and B', value: 'Independent A / B' },
    { label: 'Bus continuous limit', detail: 'Verify installation derating', value: '4,000 A per bus' },
    { label: 'Tap-off continuous limit', detail: '12 monitored feeders in 6 pairs', value: '800 A per feeder' },
    { label: 'Rectifiers', detail: 'N+1 allowance of 3.2 MW, also limited by bus ampacity', value: '9 × 400 kW per path' },
    { label: 'Redundant bus capacity', detail: 'One surviving path, out of 6.4 MW installed across both', value: '3.2 MW' },
    { label: 'AC power-quality meters', detail: '5 locations on each of 2 paths', value: '10' },
    { label: 'DC power meters', detail: '2 DC mains and 12 inverter feeds', value: '14' },
    { label: 'Thermal channels', detail: '12 joints and 4 conductor channels', value: '16' },
    { label: 'Insulation monitors', detail: 'One on each isolated bus', value: '2' },
    { label: 'Residual-current sensors', detail: 'DC-capable; AC+DC RMS', value: '2' }
];

const scenario = [
    {
        step: '01',
        title: 'A joint starts running warm',
        body: 'Joint J04-A climbs to about 63 °C, around 35 K above its local ambient, while its neighbours sit in the mid-40s. The voltage drop across it puts its estimated resistance at about twice theirs, and it is rising faster than any of them.'
    },
    {
        step: '02',
        title: 'An advisory goes up',
        body: 'It crosses the 60 °C advisory limit. The advisory names the joint, the feeder and the reason, next to the current, the rate of rise and what its paired feed would carry if this one were lost. The system itself trips nothing.'
    },
    {
        step: '03',
        title: 'Someone goes and looks',
        body: 'Acknowledging it records that someone saw it, and the condition stays active. The answer is an inspection arranged under the site’s own electrical work procedures, while it is still a maintenance job.'
    }
];

const rules = [
    {
        title: 'It watches. It does not switch.',
        body: 'There are no switching or trip controls anywhere in the system. It can show you what the equipment is doing, and it cannot tell the equipment what to do.'
    },
    {
        title: 'Protection stays where it is',
        body: 'Breakers, relays and the installed control system keep doing the protecting, and any trip is theirs. Monitoring sits beside them and does not replace them.'
    },
    {
        title: 'Missing data stays visible',
        body: 'A last-known value never poses as a current one. A stale channel is labelled and left out of the totals, and an instrument that is not fitted says “Not instrumented”, not zero.'
    },
    {
        title: 'Every reading shows its basis',
        body: 'Each value carries its source timestamp, its quality flag and how it was acquired, and a calculated result inherits the age and quality of what went into it.'
    },
    {
        title: 'Fast events need fast hardware',
        body: 'A display refreshing every 2 seconds cannot see a 12 ms dip. Fast events come from recorders at the equipment, with their own sampling, and never from the dashboard’s polling.'
    },
    {
        title: 'Estimates are called estimates',
        body: 'Measured, derived, status and configured values are kept apart. A live joint resistance is an estimate under load, not a four-wire micro-ohmmeter test on a de-energized joint.'
    },
    {
        title: 'A reading is not a certificate',
        body: 'Checks run against limits you set. IEEE 519 and IEC 61000-4-30 need a defined point of common coupling, a demand basis, documented instruments and proper observation periods, so a spot reading earns no pass or fail.'
    },
    {
        title: 'Installed is not redundant',
        body: 'Two 3.2 MW paths are 6.4 MW installed and 3.2 MW redundant. The number that matters is what one surviving path can carry, so that is the number the system reports.'
    }
];

const dataFlow = [
    {
        step: '01',
        title: 'Field instruments',
        body: 'Power-quality meters at the incomer, rectifier input, bypass, UPS output and PDU, plus isolated DC meters, thermal sensors, insulation monitors and the BMS. Acquisition stays local to each device.'
    },
    {
        step: '02',
        title: 'Edge gateways',
        body: 'Read compatible Modbus RTU/TCP registers, then normalize units, device identity, timestamps and quality flags.'
    },
    {
        step: '03',
        title: 'Site data platform',
        body: 'Publish through an approved MQTT, OPC UA or HTTPS integration into the historian and the facility’s monitoring system.'
    },
    {
        step: '04',
        title: 'Operator dashboard',
        body: 'Read-only measurements, trends and an alarm workflow, showing the freshness and coverage of every channel.'
    }
];

const commissioning = [
    { title: 'Instrument inventory', text: 'Meter and sensor IDs, serial numbers, firmware, locations, ratios, ranges and polarity.' },
    { title: 'Measurement chain', text: 'CT and VT ratios, polarity, the neutral channel and phase mapping, checked through to the display.' },
    { title: 'Alarm configuration', text: 'Approved thresholds, delays, hysteresis and reset policy, reviewed for the actual equipment.' },
    { title: 'Time and calibration', text: 'Clock source and accuracy, calibration and due dates, and transducer uncertainty.' },
    { title: 'Coverage and retention', text: 'Which points are expected, how quality is flagged, and how data is acquired and stored.' },
    { title: 'An approved test plan', text: 'Load steps, battery and bypass transfers, with synchronized recordings of input and output.' }
];

const faqs = [
    {
        question: 'How do we get SmartDC for our data center?',
        answer: 'Talk to us. We look at your electrical system, map the measurement points to your equipment and scope the instruments and integration from there. It is easiest on a new build, because measurement points go on the drawings before the equipment is built, and it can also be fitted to an existing facility.'
    },
    {
        question: 'Does it monitor my servers?',
        answer: 'It monitors the power feeding them, not the servers themselves. It will not tell you about CPU load, software or networks. It tells you what is happening in the electrical system underneath all of that.'
    },
    {
        question: 'Can it turn anything on or off?',
        answer: 'No. It has no switching or trip controls. Protection and switching stay in the installed control system.'
    },
    {
        question: 'Does it have to be 800 VDC?',
        answer: 'No. The reference design shown here uses three-phase AC distribution and an 800 V floating DC link inside the UPS. Voltage class, grounding, ratings, sensors and operating limits all get mapped to your actual installation.'
    },
    {
        question: 'Will it work with the meters I already have?',
        answer: 'It reads compatible meters over Modbus and publishes over MQTT, OPC UA or HTTPS. Compatibility depends on the equipment, so we check yours during scoping rather than promise it from here.'
    },
    {
        question: 'Will it prove I comply with IEEE 519?',
        answer: 'No. It can compare readings with limits you set and keep the evidence, but compliance needs a defined point of common coupling, a demand basis and the proper observation periods. The system does not issue a pass or fail.'
    },
    {
        question: 'What does it do when something actually fails?',
        answer: 'It raises the alarm from the readings themselves and shows what changed, what is still protected, how much margin is left and how long the batteries would last. It does not act on the fault: protection stays with your breakers and control system. The fault simulator in the demo shows this for five failures.'
    },
    {
        question: 'Are the numbers in the demo real?',
        answer: 'No. The demo runs on a simulated data center, so every reading in it is simulated, and anything you export from it is marked that way. The ratings and limits on this page are a reference. On your system, the values come from your equipment and your engineering.'
    },
    {
        question: 'Who do I talk to about it?',
        answer: `Us. Call ${contact.phone} or request a consultation, and tell us what you are building or upgrading.`
    }
];

export default function DataCenterMonitoring() {
    return (
        <>
            <Seo
                title="SmartDC: Data Center Power Monitoring"
                description="SmartDC is data center power monitoring from Smart Electric Solutions: AC power quality, the UPS DC bus, connection temperatures, insulation, batteries and backup capacity, in one read-only system. Try the live dashboard demo."
                keywords="SmartDC, data center power monitoring, power quality monitoring, UPS monitoring, 800 VDC, busbar temperature monitoring, insulation monitoring, harmonics, electrical contractor South Florida"
                path={EXPLAINER_PATH}
                schema={{
                    '@type': 'WebPage',
                    name: 'SmartDC: Data Center Power Monitoring',
                    description: 'SmartDC is data center power monitoring from Smart Electric Solutions.',
                    url: `${site.origin}${EXPLAINER_PATH}`
                }}
            />

            <PageHero
                eyebrow="SmartDC · Data center power monitoring"
                title="The power behind your servers, measured end to end."
                lead="SmartDC is one place to see the electricity feeding your servers, from the utility connection through the UPS to the rack, and to catch what is going wrong while it is still an advisory and not an outage."
                backdrop={SwitchgearLineups}
                focus="40% 50%"
                crumbs={[{ label: 'Systems' }, { label: 'SmartDC Monitoring' }]}
                actions={
                    <>
                        <Link className="btn btn--accent" to={DEMO_PATH}>
                            Open the demo
                            <span className="btn__arrow" aria-hidden="true">→</span>
                        </Link>
                        <Link className="btn btn--ghost" to="/#contact">
                            Talk to us about a data center
                            <span className="btn__arrow" aria-hidden="true">→</span>
                        </Link>
                    </>
                }
            />

            <TradeTicker />

            <section className="section">
                <div className="shell">
                    <SectionHead
                        eyebrow="What this is"
                        title="A window onto the power, not a remote control."
                    />

                    <Reveal className="prose" y={24} delay={0.1}>
                        <p>A data center depends on a long chain of electrical equipment: the utility connection, the UPS that converts power to DC and back, the batteries that carry the load through a failure, and the distribution that reaches every rack. A problem rarely starts where it finally shows up. A connection can run warm long before anything trips, and a disturbance on the incoming supply only matters if it gets through to the servers.</p>
                        <p>SmartDC measures that whole chain and brings it together in one place, on both independent power paths. It watches the electrical system, not the servers’ software or network: if you already monitor those, this is what watches the power underneath them.</p>
                    </Reveal>

                    <div style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                        <Stats items={[
                            { value: '99', label: 'Measurements defined' },
                            { value: '15', label: 'Disciplines' },
                            { value: '2', label: 'Independent paths' },
                            { value: '2 s', label: 'Dashboard refresh' }
                        ]} />
                    </div>
                </div>
            </section>

            <section className="section section--ruled" id="path">
                <div className="shell">
                    <SectionHead
                        eyebrow="Where it measures"
                        title="Five AC meters and a monitored DC link, on each path."
                        lead="Power reaches a server through a chain, and each point on it answers a different question. This is one path. A second, independent copy runs beside it, with no DC tie between them, so losing one leaves the other carrying the load."
                    />

                    <div style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                        <PowerPath />
                    </div>

                    <div style={{ marginTop: 'clamp(3rem, 7vw, 5rem)' }}>
                        <SectionHead
                            eyebrow="The reference design"
                            title="The numbers it is drawn around."
                            lead="A reference, not a limit. Voltage class, grounding, ratings and sensor selection all get mapped to your installation."
                        />

                        <Reveal as="dl" className="spec" y={22} amount={0.05}>
                            {reference.map(item => (
                                <div className="spec__row" key={item.label}>
                                    <dt>
                                        {item.label}
                                        <span className="spec__detail">{item.detail}</span>
                                    </dt>
                                    <dd>{item.value}</dd>
                                </div>
                            ))}
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="section section--ruled" id="what-it-does">
                <div className="shell">
                    <SectionHead
                        eyebrow="What it does"
                        title="One system, from the utility to the rack."
                        lead="It follows the power through every stage, tests readings against your limits, and shows how far each one can be trusted. All of it runs in the demo, on a simulated data center in your browser, and nothing you do there touches any equipment."
                    />

                    <Reveal className="screen-index__cta" y={18} delay={0.14}>
                        <Link className="btn btn--accent" to={DEMO_PATH}>
                            Open the demo
                            <span className="btn__arrow" aria-hidden="true">→</span>
                        </Link>
                    </Reveal>

                    {SCREEN_GROUPS.map(group => (
                        <div className="screen-index__group" key={group}>
                            <h3 className="screen-index__label">{group}</h3>

                            <ul className="screen-index__cards">
                                {SCREENS.filter(screen => screen.group === group).map((screen, i) => (
                                    <Reveal as="li" className="screen-index__card" key={screen.id} delay={(i % 3) * 0.06} y={20} amount={0.2}>
                                        <h4 className="screen-index__title">{screen.title}</h4>
                                        <p className="screen-index__copy">{screen.text}</p>
                                        <Link className="screen-index__link" to={demoLink(screen.id)} aria-label={`Open ${screen.title} in the demo`}>
                                            Open in the demo →
                                        </Link>
                                    </Reveal>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>

            <ProcessRail eyebrow="A worked example" title="A warm joint, start to finish." items={scenario} />

            <div className="shell">
                <Reveal className="demo-band" y={20}>
                    <div>
                        <h2>That joint is running in the demo.</h2>
                        <p>Open Thermal health, choose J04-A, and follow it from the map to the feeder, the advisory and what its paired feed would carry.</p>
                    </div>
                    <Link className="btn btn--accent" to={demoLink('thermal')}>
                        See it for yourself
                        <span className="btn__arrow" aria-hidden="true">→</span>
                    </Link>
                </Reveal>
            </div>

            <div className="shell">
                <Reveal className="demo-band" y={20}>
                    <div>
                        <h2>Break it on purpose.</h2>
                        <p>The fault simulator takes out a utility feed or a whole UPS path, overheats a joint, fails a rectifier module or faults the insulation. Every reading, diagram and alarm responds, and nothing touches any equipment.</p>
                    </div>
                    <Link className="btn btn--accent" to={demoLink('simulator')}>
                        Run a fault
                        <span className="btn__arrow" aria-hidden="true">→</span>
                    </Link>
                </Reveal>
            </div>

            <section className="section section--raised" id="alarms">
                <div className="shell">
                    <SectionHead
                        eyebrow="Alarms"
                        title="Thresholds, delays and a way back."
                        lead="An alarm that resets as soon as a reading dips is noise, so each limit comes with a delay and a separate reset point. These are starting points for monitoring, not protective relay or breaker settings, and they get reviewed against your equipment."
                    />

                    <ul className="limits" style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                        {THRESHOLDS.map((limit, i) => (
                            <Reveal as="li" className="limits__card" key={limit.label} delay={(i % 4) * 0.06} y={22}>
                                <span className="limits__label">{limit.label}</span>
                                <span className="limits__value">{limit.value}</span>
                                <span className="limits__note">{limit.note}</span>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="section section--ruled" id="measurements">
                <div className="shell">
                    <SectionHead
                        eyebrow="Everything it measures"
                        title="Every measurement, defined."
                        lead="Ninety-nine definitions across fifteen disciplines. Which ones apply depends on the sensors fitted at your site, and the cadences are targets. Open a group for the detail."
                    />

                    <div style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                        <MeasurementCatalog groups={measurementGroups} />
                    </div>
                </div>
            </section>

            <Bento
                eyebrow="How it behaves"
                title="The rules it is built on."
                lead="A monitoring system is only useful if you can believe it, so most of the design is about being honest with what it knows and what it does not."
                items={rules}
            />

            <ProcessRail eyebrow="How the data travels" title="From the sensor to the screen." items={dataFlow} />

            <ServiceList
                id="commissioning"
                eyebrow="Commissioning"
                title="What goes into going live."
                lead="Software alone cannot make a reading trustworthy, so commissioning builds these records with you before it goes live on your equipment. The workspace evaluates records; it does not create disturbances, run a load bank or switch your UPS."
                items={commissioning}
            />

            <section className="section section--ruled">
                <div className="shell">
                    <SectionHead
                        eyebrow="Why us"
                        title="This is electrical work before it is software."
                    />

                    <Reveal className="prose" y={24} delay={0.1}>
                        <p>Most of what is on this page has to be wired into the electrical system: current sensors on the bus and feeders, isolated voltage inputs, thermal sensors on the joints, insulation monitors on each bus. Fitted afterwards, that work competes with live equipment for space and access. Planned from the start, it goes on the same drawings as everything else.</p>
                        <p>That is why it belongs with our work on the{' '}
                            <Link to="/tpi">TPI partnership</Link>. We already plan the switchboards, the switchgear and the crew that installs them as one job from the first drawing. Monitoring belongs in that same conversation.
                        </p>
                    </Reveal>
                </div>
            </section>

            <Faqs items={faqs} />

            <Cta
                eyebrow="Data centers"
                title="Plan SmartDC with the power."
                lead="Planning or upgrading a data center anywhere in South Florida? Tell us what you are building, and we will show you how it fits."
            />
        </>
    );
}
